import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { API_URL } from '../config/api';
import { adminFetch } from '../utils/adminFetch';
import AdminNav from './AdminNav';
import { isRemembered, remember } from '../utils/affiliateAuth';
import './AffiliateOverview.css';

// 온보딩 퍼널 (GET /api/stripe/onboarding-funnel). 초대~답장은 recruiting.sqlite,
// 등록·게시는 affiliates Mongo — 서버가 합쳐서 내려준다.
type FunnelStallRow = { email?: string; username?: string; days: number; reminders?: number; followups?: number };
type FunnelData = {
  periodDays: number;
  counts: { invited: number; joined: number; welcomed: number; replied: number;
    contracted: number; registered: number; posted: number };
  stalled: {
    notJoined?: { total: number; rows: FunnelStallRow[] };
    noReply?: { total: number; rows: FunnelStallRow[] };
    // 답장까지 해놓고 계정정보를 안 낸 사람 = 실측 최대 누수 구간(2026-09-14 신설).
    noAccount?: { total: number; rows: FunnelStallRow[] };
  };
};

// One affiliate's rolled-up numbers (mirrors POST /api/stripe/affiliate-overview).
interface OverviewRow {
  route: string;
  handle: string;      // 편집·매칭용(=route). profile PATCH·drafts 키.
  igHandle?: string;   // 화면 표시 전용(실제 IG 핸들). 없으면 handle 폴백.
  promoCode: string;
  totalSignups: number;
  paidCustomers: number;
  // 유료 전환 고객의 국가 분포(Stripe 청구지, 내림차순). Audience 칼럼(top3 국기+호버 %)용.
  countryDistribution?: { country: string; count: number }[];
  totalRevenue: number;
  bonus: number;
  blendedRate: number;
  totalPaid: number;
  retainerPending?: number | null;    // 밀린 기본급(완료 주차 기준)
  retainerOwedWeeks?: number[];       // 밀린 주차 배열
  bonusPending?: number | null;       // 미지급 인센(양수=지급 대상)
  payoutStatus?: 'enabled' | 'started' | 'none';  // Stripe 온보딩 상태(테이블 표시)
  w9Status?: 'none' | 'requested' | 'received' | 'not_required';  // W-9 세무폼 수집 상태(Inkle Books)
  bioStatus?: 'ok' | 'missing_ref' | 'missing_coupon' | 'missing_both' | 'no_account' | 'private' | 'error' | 'not_checked';  // IG 바이오에 ref링크·쿠폰코드 넣었는지 자동체크(runBioCheck.js, 매시간)
  creditStatus?: 'granted' | 'signed_up' | 'not_found' | 'no_email';  // Ssemble 크레딧: ssembleSignupEmail로 users DB 조회 자동판정
  credits?: number | null;  // credits.shorts_video + referralCredits.shorts_video 잔고(가입 확인 시)
  ssembleSignupEmail?: string | null;  // 크레딧을 지급할 ssemble.com 가입 이메일(없으면 지급 불가)
  // 우리가 실제 지급한 수량(credit_logs 실측). 표에는 안 띄운다 — 일상 판단은 Credits(잔고)로 하고,
  // 이 값은 "얼마 줬나"를 따질 때만 필요해서 API·DB에만 남긴다(2026-09-11 태준 판단).
  creditsGranted?: number | null;
  sheetUrl?: string | null;  // 개인 구글시트 링크(임시, 시트→DB 정본화 검증용)
  // Editable metadata (persisted via PATCH /affiliate-profile/:handle)
  email: string;
  status: 'active' | 'inactive' | 'ended';
  // 운영 메모. status만으론 안 보이는 진행 상태를 적는 칸 — 특히 종료자의 계정 회수 단계
  // ("로그아웃 대기" → "2FA 리셋 완료")를 여기 남겨야 백로그가 묻히지 않는다.
  notes?: string;
  // Onboarding lifecycle (UGC creators). stage='' for established affiliates.
  stage?: string;             // '' | 'warmup' | 'first_posted' | 'steady' | ...
  warmupStartedAt?: string | null;  // 시작 원점(구 d0Date 통합) — 있으면 스케줄러 phase로 단계 파생
  // 동기화 최신화 시각(Reels Synced / DM Synced 칼럼). 게시·대화 판정이 얼마나 신선한 데이터
  // 위에서 이뤄지는지 보여준다. null = 그 크리에이터에 대해 아직 한 번도 안 돌았음.
  reelsSyncedAt?: string | null;
  convoSyncedAt?: string | null;
  hasAccount?: boolean;       // UGC 계정 credentials 등록 여부(🔑 버튼 상태)
  // Enriched from the recruiting server's sent_emails_log (synced offline).
  country: string;
  ageRange: string;
  ethnicity: string;
  tiktokUrl: string;
  // Instagram reel play-count buckets by post date (synced by cron).
  views30d: number;
  viewsPrev30d: number;
  views7d: number;
  viewsPrev7d: number;
  reelCount: number;
  // 누적 조회수(전 기간) + 누적 릴 수. 30일 버킷과 달리 기간 컷이 없다.
  viewsAll: number;
  reelCountAll: number;
  // 최근 30일 지급액(AffiliatePayment.date 기준). CPM 분자 — totalPaid(누적)를
  // 30일 조회수로 나누면 기간 불일치로 장기 근속자가 최대 3.9배 나쁘게 찍힌다.
  paid30d: number;
  error: string | null;
}

interface OverviewTotals {
  totalSignups: number;
  paidCustomers: number;
  totalRevenue: number;
  bonus: number;
  totalPaid: number;
  payout30d: number;
  // 합계행 블렌디드 RPM/CPM용. payout30d(전체 지급 총액)와 paid30d(행 합계)는 별개 —
  // payout30d는 서버 단일 집계라 active-only 필터에 안 걸리고, paid30d는 표시된 행만 더한다.
  views30d: number;
  viewsAll: number;
  paid30d: number;
}

// recruiting 서버(ssemble-marketing recruitingServer.js)는 dstribute.ai/recruit 로 리버스프록시됨(SentEmails.tsx와 동일).
const RECRUIT_API = '/recruit/api';

// 발굴 키워드(recruit API POST /found-via): recruiting.sqlite의 콜드메일 발송 로그를 이메일로 조인해
// 그 사람을 처음 찾아낸 검색어(최초 귀속)와 전체 검색어. 없으면 인바운드·수동 영입.
interface FoundVia {
  username: string;
  first_keyword: string | null;
  first_date: string | null;
  keywords: string[];
  sent_at: string | null;
  emailed: boolean;
}

interface OverviewData {
  affiliates: OverviewRow[];
  totals: OverviewTotals;
}

type SortKey =
  | 'route' | 'totalSignups' | 'paidCustomers' | 'convRate' | 'totalRevenue'
  | 'bonus' | 'status' | 'views30d' | 'viewsAll' | 'rpm' | 'cpm'
  | 'stage' | 'startedAt';

const formatUSD = (amount: number): string =>
  amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const pct = (rate: number): string => `${Math.round(rate * 100)}%`;

// Conversion rate with one decimal ("6.2%") — signup→paid rates are small, so
// integer rounding would collapse most of the roster into the same bucket.
const pct1 = (rate: number): string => `${(rate * 100).toFixed(1)}%`;

// Signup→paid conversion. null when there are no signups to divide by (promo-only
// customers can exist without tracked signups, so this stays a "—", not 0%).
const convRateOf = (r: { totalSignups: number; paidCustomers: number }): number | null =>
  r.totalSignups > 0 ? r.paidCustomers / r.totalSignups : null;

// 조회수 K/M 약어. 표에서 자릿수가 들쭉날쭉하면 비교가 안 돼 3자리로 압축한다.
const formatViews = (v: number): string => {
  if (!v) return '0';
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(v >= 10_000_000 ? 0 : 1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(v >= 10_000 ? 0 : 1)}K`;
  return String(v);
};

// 직전 동일기간 대비 증감률. 직전이 0이면 비교 불가(신규 시작)라 null.
const deltaOf = (cur: number, prev: number): number | null =>
  prev > 0 ? (cur - prev) / prev : null;

// RPM = 조회수 1,000회당 매출. 분모(조회수)가 없으면 null → 표에서 '—'.
// 매출 자체가 0인 건 유효한 0(게시는 했는데 전환이 없음)이라 0을 그대로 돌려준다.
const rpmOf = (r: { totalRevenue: number; views30d: number }): number | null =>
  r.views30d > 0 ? (r.totalRevenue / r.views30d) * 1000 : null;

// CPM = 조회수 1,000회당 비용. ⚠️ 분자는 paid30d(최근 30일 지급액)지 totalPaid(누적)가 아니다.
// 누적을 30일 조회수로 나누면 장기 근속자가 최대 3.9배 비싸게 찍힌다(2026-09-11 실측).
const cpmOf = (r: { paid30d: number; views30d: number }): number | null =>
  r.views30d > 0 ? ((r.paid30d || 0) / r.views30d) * 1000 : null;

// Paid customers' country distribution -> top-3 flags + a tooltip with every
// share. 'Unknown' (Stripe billing country not captured) is excluded from the
// flags but kept in the tooltip so coverage gaps stay visible.
const audienceSummary = (
  dist?: { country: string; count: number }[]
): { flags: string; title: string } | null => {
  if (!dist || dist.length === 0) return null;
  const total = dist.reduce((s, d) => s + d.count, 0);
  if (total === 0) return null;
  const known = dist.filter(d => d.country !== 'Unknown' && flagEmoji(d.country));
  if (known.length === 0) return null;
  const share = (n: number) => `${Math.round((n / total) * 100)}%`;
  const parts = known.slice(0, 8).map(d => `${flagEmoji(d.country)} ${d.country} ${share(d.count)} (${d.count})`);
  const unknown = dist.find(d => d.country === 'Unknown');
  if (unknown) parts.push(`미상 ${share(unknown.count)} (${unknown.count})`);
  return {
    flags: known.slice(0, 3).map(d => flagEmoji(d.country)).join(' '),
    title: parts.join('\n')
  };
};

const audienceCell = (dist?: { country: string; count: number }[]) => {
  const a = audienceSummary(dist);
  return a
    ? <span className="affiliate-overview__audience" title={a.title}>{a.flags}</span>
    : <span className="affiliate-overview__muted">—</span>;
};

// ISO 3166-1 alpha-2 code -> flag emoji (regional indicator letters).
// "US" -> "🇺🇸". Returns "" for anything that isn't two ASCII letters.
const flagEmoji = (code: string): string => {
  const cc = (code || '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(cc)) return '';
  return String.fromCodePoint(...[...cc].map(c => 0x1f1e6 + (c.charCodeAt(0) - 65)));
};

// Ethnicity label (stored lowercase, e.g. "east_asian") -> a person emoji with a
// Unicode skin-tone modifier. Best-effort visual cue; not every label maps to a
// distinct tone, so this is a rough marker, not a precise depiction. Unknown /
// unmapped values fall back to "❓".
const ethnicityEmoji = (value: string): string => {
  switch ((value || '').trim().toLowerCase()) {
    case 'white': return '🧑🏻';
    case 'east_asian': return '🧑🏼';
    case 'south_asian': return '🧑🏽';
    case 'arab': return '🧑🏽';
    case 'hispanic': return '🧑🏽';
    case 'black': return '🧑🏿';
    default: return '❓'; // unknown / empty
  }
};

// Every affiliate with ≥$100 cumulative bonus (2026-09 snapshot), with the
// country and the recruiting search keyword that first surfaced them
// (recruiting.sqlite: sent_emails_log × creator_found_via, earliest keyword).
// kw '' = never cold-emailed (inbound/manual). Static snapshot — re-run the
// email-join against the recruiting DB to refresh. Drives the rules below.
// Shape returned by POST /api/stripe/winner-yield-recompute (backend proxy to
// the recruiting server's winnerYield.js --write --json).
type RecomputeResult = {
  ok: boolean;
  computed_at?: string;
  total_affiliates?: number;
  winners?: number;
  categories?: { cat: string; sent: number; winners: number; per1k: number }[];
  regions?: { region: string; sent: number; winners: number; per1k: number }[];
  weights?: Record<string, number>;
  wrote?: boolean;
  error?: string;
};

const TOP_PERFORMERS: { route: string; c: string; bonus: number; kw: string }[] = [
  { route: 'joe', c: 'GB', bonus: 7922, kw: 'actuarial · resume' },
  { route: 'harrison', c: 'GB', bonus: 2133, kw: 'guy ugc' },
  { route: 'tillmann', c: 'US', bonus: 1772, kw: '' },
  { route: 'kyri10', c: 'US', bonus: 1297, kw: '' },
  { route: 'samuel', c: 'US', bonus: 1049, kw: 'ugcbro' },
  { route: 'jonathan', c: 'CA', bonus: 757, kw: 'tipping culture' },
  { route: 'patrick', c: 'US', bonus: 664, kw: 'trackathlete' },
  { route: 'max', c: 'GB', bonus: 393, kw: 'esportscoachvloghouse' },
  { route: 'tim', c: 'US', bonus: 387, kw: 'calisthenicspark' },
  { route: 'jacob', c: 'CA', bonus: 288, kw: 'guy ugc' },
  { route: 'ben', c: 'US', bonus: 206, kw: 'menugctips' },
  { route: 'nate', c: 'US', bonus: 206, kw: 'wingman' },
  { route: 'christian', c: 'US', bonus: 190, kw: 'serper:ironman' },
  { route: 'josh', c: 'US', bonus: 166, kw: 'linebackerlift' },
  { route: 'federico', c: 'IT', bonus: 165, kw: 'hairtutorial' },
  { route: 'matt', c: 'US', bonus: 140, kw: 'tradingcards' },
  { route: 'levi', c: 'DE', bonus: 114, kw: 'ego lifting' },
  { route: 'anthony', c: 'GB', bonus: 102, kw: 'darts' }
];

// Compact number: 1500 -> "1.5K", 2_300_000 -> "2.3M". Drops a trailing ".0".
const formatCompact = (n: number): string => {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return String(n);
};

// Instagram handles may carry trailing punctuation (e.g. "parker.clips_"); the
// profile URL works with the handle as-is, so no cleanup needed.
// 검색어 접두 표시: handle: = 유저명 검색(핸들 검색 워커), serper: = 구글 검색 발굴.
const keywordLabel = (k: string): string => k.replace(/^handle:/, '🔎 ').replace(/^serper:/, 'G· ');
const foundViaCell = (fv?: FoundVia) => {
  if (!fv || !fv.first_keyword) {
    return <span className="affiliate-overview__muted" title={fv ? '발굴은 됐지만 검색어 기록 없음' : '콜드메일 발송 기록 없음 — 인바운드·수동 영입'}>—</span>;
  }
  const rest = fv.keywords.slice(1);
  const title = [
    `@${fv.username} · 최초 발굴 ${fv.first_date || '?'}`,
    ...(rest.length ? [`다른 검색어 ${rest.length}개: ${rest.slice(0, 12).map(keywordLabel).join(', ')}${rest.length > 12 ? ' …' : ''}`] : []),
  ].join('\n');
  return (
    <span title={title}>
      {keywordLabel(fv.first_keyword)}
      {rest.length ? <span className="affiliate-overview__muted"> +{rest.length}</span> : null}
    </span>
  );
};

const instagramUrl = (handle: string): string =>
  `https://instagram.com/${handle.replace(/^@/, '')}`;

// ── 동기화 최신화 시각 (Reels Synced / DM Synced 칼럼) ──────────────────────
// 서버는 UTC ISO로 주고 화면은 KST로 보여준다(운영자가 한국 시간으로 판단). 날짜만으론
// "오늘 돌았나"를 못 가리니 분까지 찍는다 — 루프가 매시간이라 시각이 핵심 정보다.
const KST_FMT = new Intl.DateTimeFormat('ko-KR', {
  timeZone: 'Asia/Seoul', month: '2-digit', day: '2-digit',
  hour: '2-digit', minute: '2-digit', hour12: false,
});
// "09/11 14:32" 형태로 정규화(ko-KR 기본 출력의 마침표·공백을 걷어낸다).
const fmtKST = (iso: string): string => {
  const parts = KST_FMT.formatToParts(new Date(iso));
  const get = (t: string) => parts.find(p => p.type === t)?.value || '';
  return `${get('month')}/${get('day')} ${get('hour')}:${get('minute')}`;
};
const hoursSince = (iso: string): number =>
  (Date.now() - new Date(iso).getTime()) / 3600000;
// 신선도 등급: 릴은 하루 1회 크론이라 36h, DM은 매시간 루프라 6h를 기준으로 늦으면 경고색.
// 임계를 넘으면 그 값으로 내린 판정(게시 미달·미답 여부)이 낡은 데이터 위에 있다는 신호다.
// ⚠️ 빈칸이 두 가지 뜻으로 읽히면 안 된다(2026-09-11). 예전엔 "종료자라 원래 안 돎"과
//   "동기화가 고장남"이 똑같이 `—`로 보여, 실제로 신규 4명이 사각지대에 빠진 걸
//   빈칸만 보고는 구분할 수 없었다. 이제 대상 밖은 `·`(회색 점)로, 진짜 누락은 `—`로 가른다.
//   판정: 종료자(inactive/ended) 또는 commission_only = 수집 대상 아님 → 빈칸이 정상.
const isOutOfSyncScope = (row: { status?: string; stage?: string }): boolean => {
  const st = String(row.status || '').toLowerCase();
  const sg = String(row.stage || '').toLowerCase();
  return st === 'inactive' || st === 'ended' || sg === 'offboard' || sg === 'commission_only';
};
const syncCell = (
  iso: string | null | undefined,
  staleH: number,
  row: { status?: string; stage?: string } = {}
): React.ReactNode => {
  if (!iso) {
    if (isOutOfSyncScope(row)) {
      return (
        <span
          className="affiliate-overview__sync-na"
          title="동기화 대상 아님 — 종료·커미션전용 크리에이터는 릴 수집을 돌지 않는다(빈칸이 정상)"
        >·</span>
      );
    }
    return (
      <span
        className="affiliate-overview__sync--never"
        title="🚨 아직 한 번도 동기화되지 않음 — active인데 이 값이 비면 그 크리에이터는 게시·대화가 전혀 수집되지 않는 사각지대다(등록 시 affiliateStatus 누락 등)"
      >—</span>
    );
  }
  const h = hoursSince(iso);
  const cls = h > staleH * 2 ? 'dead' : (h > staleH ? 'stale' : 'fresh');
  const ago = h < 1 ? '방금' : (h < 24 ? `${Math.floor(h)}시간 전` : `${Math.floor(h / 24)}일 전`);
  return (
    <span className={`affiliate-overview__sync affiliate-overview__sync--${cls}`} title={`${ago} · ${new Date(iso).toLocaleString()} (KST 표시, 기준 ${staleH}h)`}>
      {fmtKST(iso)}
    </span>
  );
};

// Onboarding-stage badge. UGC creators being onboarded carry a `stage`
// ('warmup' | 'first_posted' | 'steady' | …); established affiliates have ''.
// Warm-up shows how many days in (from warmupStartedAt) so laggards stand out.
const daysSince = (iso?: string | null): number | null => {
  if (!iso) return null;
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return Number.isFinite(d) ? d : null;
};
// 진행 주차. 정본 = detectPayouts.js 의 `week` 계산과 동일한 식:
//   daysSince = floor((now - start)/86400000), week = floor(daysSince/7) + 1
// 즉 시작 당일이 W1이고, 7일이 지나면 W2가 된다. 지급 판정에 쓰이는 "완료 주차"
// (completedWeeks = floor(daysSince/7))와는 1 차이 — 여기 표시하는 건 지금 굴러가고
// 있는 주차라서 정산 금액과 직접 비교하면 안 된다.
// ⚠️ 시작 원점은 warmupStartedAt. detectPayouts는 retainerStartAt을 우선 쓰지만
//   이 API(affiliate-overview)는 retainerStartAt을 안 내려준다 — 무급 웜업이 따로 있는
//   크리에이터는 여기 주차가 정산 주차보다 앞설 수 있다.
const weekFromStart = (iso?: string | null): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return null;
  const days = Math.floor((Date.now() - t) / 86400000);
  if (days < 0) return null;            // 시작일이 미래 = 아직 시작 전
  return Math.floor(days / 7) + 1;
};

// 스케줄러 규칙(ugcScheduler): Day0=create, 1-7=warmup, 8-10=ramp, 11+=full.
const phaseFromD0 = (d0?: string | null): string | null => {
  if (!d0) return null;
  const day = Math.max(0, Math.floor((Date.now() - new Date(d0).getTime()) / 86400000));
  if (day === 0) return 'create';
  if (day <= 7) return 'warmup';
  if (day <= 10) return 'ramp';
  return 'full';
};
const stageBadge = (r: { stage?: string; warmupStartedAt?: string | null; status?: string; hasAccount?: boolean }): React.ReactNode => {
  const s = r.stage || '';
  if (!s) {
    // stage 미기입. 판정 순서: ①이탈/종료 상태 → ②스케줄러 phase(warmupStartedAt) → ③계정받음=온보딩중 → ④신호없음(—).
    // inactive/ended는 온보딩이 아니라 이탈/종료라 회색으로 명시(— 로 숨기지 않는다).
    if (r.status === 'inactive') {
      return <span className="affiliate-overview__stage affiliate-overview__stage--idle">inactive</span>;
    }
    if (r.status === 'ended') {
      return <span className="affiliate-overview__stage affiliate-overview__stage--end">ended</span>;
    }
    // warmupStartedAt(시작일) 있으면 스케줄러 phase에서 단계를 파생해 표시.
    // (구 d0Date는 2026-09-02 warmupStartedAt으로 통합 — 값이 같은 시작 원점.)
    const ph = phaseFromD0(r.warmupStartedAt);
    if (ph) {
      const phLabel: Record<string, string> = { create: '🆕 Setup', warmup: '🌡️ Warming up', ramp: '📈 Ramping', full: '🎬 Posting' };
      const phCls = ph === 'warmup' ? 'warm' : 'live';
      return <span className={`affiliate-overview__stage affiliate-overview__stage--${phCls}`}>{phLabel[ph]}</span>;
    }
    // 계정정보를 받았으면(igAccounts 등록) 웜업 시작 전이라도 '온보딩 중'으로 표시 — —로 숨기지 않는다.
    if (r.hasAccount) {
      return <span className="affiliate-overview__stage affiliate-overview__stage--onboard">🚀 Onboarding</span>;
    }
    // 아무 신호도 없음(기존 어필리에이트 등).
    return <span className="affiliate-overview__muted">—</span>;
  }
  const d = daysSince(r.warmupStartedAt);
  const label: Record<string, string> = {
    warmup: '🌡️ Warming up', first_posted: '🎬 Posting', steady: '📈 Steady',
    joined: '👋 Joined', account_received: '🔑 Account in', settlement: '💵 Settling',
    renewed: '🔁 Renewed', offboard: '🛑 Offboarded', commission_only: '💸 Commission only',
  };
  const cls = s === 'warmup' ? 'warm' : ((s === 'offboard' || s === 'commission_only') ? 'end' : 'live');
  return (
    <span className={`affiliate-overview__stage affiliate-overview__stage--${cls}`}>
      {label[s] || s}{s === 'warmup' && d != null ? ` · D${d}` : ''}
    </span>
  );
};

// 진행 주차 배지(W4 등). stage 배지 옆에 따로 붙인다 — 종료·이탈자에겐 의미가 없으므로
// 숨기고, 시작일이 없으면(기존 어필리에이트) 애초에 안 나온다.
const weekBadge = (r: { warmupStartedAt?: string | null; status?: string }): React.ReactNode => {
  if (r.status === 'inactive' || r.status === 'ended') return null;
  const w = weekFromStart(r.warmupStartedAt);
  if (w == null) return null;
  return (
    <span
      className="affiliate-overview__week"
      title={`시작 ${String(r.warmupStartedAt).slice(0, 10)} 기준 ${w}주차 진행중 (정산 완료주차는 이보다 1 적다)`}
    >
      W{w}
    </span>
  );
};

// A play-count cell: compact number + a green (up) / red (down) delta vs the
// previous equal-length period. No arrow when there's no prior data to compare.

// ── 계정정보(UGC 인스타 계정) 팝업 — 다중 계정(igAccounts[]) read/update ──
// 크리에이터는 정지 시 새 계정을 만들어 여러 계정을 가질 수 있어 계정별로 관리한다.
// 계정마다: username/password/mail/mailPassword/2FA/platform + isPrimary(메인) + status.
interface IgAccount {
  handle: string; username: string; password: string; mail: string;
  mailPassword: string; twoFASecret: string; platform: string;
  isPrimary: boolean; status: string; source?: string;
}
const EMPTY_ACCT: IgAccount = {
  handle: '', username: '', password: '', mail: '', mailPassword: '',
  twoFASecret: '', platform: 'instagram', isPrimary: false, status: 'active',
};
// 메모 전체보기·편집 팝업. 표의 한 줄짜리 input은 긴 메모를 못 보여준다(종료 진행상황·합의사항이
// 길게 쌓인다) → 여기서 전문을 읽고 고친다. 저장은 표와 같은 PATCH 경로를 쓴다.
const NotesModal: React.FC<{
  handle: string; initial: string; onClose: () => void; onSave: (text: string) => Promise<void>;
}> = ({ handle, initial, onClose, onSave }) => {
  const [text, setText] = useState(initial);
  const [saving, setSaving] = useState(false);
  const dirty = text !== initial;

  const save = async () => {
    setSaving(true);
    try {
      await onSave(text.trim());
      onClose();
    } finally {
      setSaving(false);
    }
  };

  // 실수로 닫아 편집분을 날리지 않도록, 내용이 바뀌었으면 백드롭 클릭·Esc에 확인을 받는다.
  const tryClose = () => {
    if (!dirty || window.confirm('저장하지 않은 변경이 있습니다. 닫을까요?')) {
      onClose();
    }
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { tryClose(); }
      // Cmd/Ctrl+Enter로 저장(긴 글 쓰다 마우스로 옮겨가지 않게).
      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && dirty) { save(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="acct-modal__backdrop" onClick={tryClose}>
      <div className="acct-modal" onClick={(e) => e.stopPropagation()}>
        <div className="acct-modal__head">
          <h3>메모 · @{handle}</h3>
          <button className="acct-modal__x" onClick={tryClose}>×</button>
        </div>
        <textarea
          className="notes-modal__area"
          value={text}
          autoFocus
          placeholder="예: 종료 통보함 · 로그아웃 스샷 대기 · 정산 완결"
          onChange={(e) => setText(e.target.value)}
        />
        <div className="acct-modal__foot">
          <span className="notes-modal__hint">{text.length}자 · ⌘/Ctrl+Enter 저장</span>
          <span>
            <button className="acct-btn" onClick={tryClose} disabled={saving}>취소</button>
            <button className="acct-btn acct-btn--save" onClick={save} disabled={saving || !dirty}>
              {saving ? '저장 중…' : '저장'}
            </button>
          </span>
        </div>
      </div>
    </div>
  );
};

const AccountModal: React.FC<{ handle: string; onClose: () => void }> = ({ handle, onClose }) => {
  const [accounts, setAccounts] = useState<IgAccount[] | null>(null);
  const [sel, setSel] = useState(0);              // 선택된 계정 인덱스
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const [msg, setMsg] = useState<string>('');

  const reload = async () => {
    try {
      const r = await adminFetch(`${API_URL}/api/stripe/affiliate-account/${encodeURIComponent(handle)}`);
      const j = await r.json();
      if (j.success) setAccounts(j.data.accounts || []);
    } catch { setMsg('불러오기 실패'); } finally { setLoading(false); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [handle]);

  const acc = accounts && accounts[sel];
  const patch = async (body: object, okMsg: string) => {
    setSaving(true); setMsg('');
    try {
      const r = await adminFetch(`${API_URL}/api/stripe/affiliate-account/${encodeURIComponent(handle)}`, {
        method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
      });
      const j = await r.json();
      if (j.success) { setAccounts(j.data.accounts || []); setMsg(okMsg); }
      else setMsg(`실패: ${j.error || ''}`);
    } catch { setMsg('저장 실패'); } finally { setSaving(false); }
  };
  const saveAcc = () => { if (acc) patch({ accountIndex: sel, fields: acc }, '저장됨 ✓'); };
  const makePrimary = () => patch({ setPrimary: sel }, '메인으로 지정됨 ✓');
  const addAccount = async () => { await patch({ addAccount: EMPTY_ACCT }, '계정 추가됨'); setSel((accounts?.length) || 0); };

  const copy = (v: string) => { navigator.clipboard?.writeText(v); setMsg('복사됨'); };
  const setField = (k: keyof IgAccount, v: string) => {
    if (!accounts) return;
    const next = accounts.slice(); next[sel] = { ...next[sel], [k]: v }; setAccounts(next);
  };
  const F: { key: keyof IgAccount; label: string; secret?: boolean }[] = [
    { key: 'username', label: 'Username' },
    { key: 'password', label: 'Password', secret: true },
    { key: 'mail', label: 'Mail' },
    { key: 'mailPassword', label: 'Mail PW', secret: true },
    { key: 'twoFASecret', label: '2FA', secret: true },
  ];
  const statusLabel = (s: string) => s === 'suspended' ? '정지' : s === 'retired' ? '종료' : '운영';

  return (
    <div className="acct-modal__backdrop" onClick={onClose}>
      <div className="acct-modal" onClick={(e) => e.stopPropagation()}>
        <div className="acct-modal__head">
          <h3>계정정보 · @{handle}</h3>
          <button className="acct-modal__x" onClick={onClose}>×</button>
        </div>
        {loading ? <p>불러오는 중…</p> : !accounts || !accounts.length ? (
          <>
            <p className="acct-empty">등록된 계정정보 없음</p>
            <button className="acct-btn acct-btn--save" onClick={addAccount} disabled={saving}>+ 계정 추가</button>
          </>
        ) : (
          <>
            {/* 계정 탭 (여러 계정) */}
            <div className="acct-tabs">
              {accounts.map((a, i) => (
                <button key={i} type="button"
                  className={`acct-tab${i === sel ? ' acct-tab--active' : ''}`}
                  onClick={() => { setSel(i); setReveal({}); }}>
                  @{a.username || a.handle || '(new)'}
                  {a.isPrimary && <span className="acct-tab__primary">메인</span>}
                  {a.status !== 'active' && <span className="acct-tab__susp">{statusLabel(a.status)}</span>}
                </button>
              ))}
              <button type="button" className="acct-tab acct-tab--add" onClick={addAccount} title="계정 추가">＋</button>
            </div>

            {acc && (<>
              {F.map(({ key, label, secret }) => {
                const val = (acc[key] as string) || '';
                const shown = !secret || reveal[key];
                return (
                  <div className="acct-row" key={key}>
                    <label>{label}</label>
                    <input type={shown ? 'text' : 'password'} value={val}
                      onChange={(e) => setField(key, e.target.value)} className="acct-input" />
                    {secret && (
                      <button className="acct-btn" type="button"
                        onClick={() => setReveal({ ...reveal, [key]: !reveal[key] })}>
                        {shown ? '숨김' : '표시'}
                      </button>
                    )}
                    <button className="acct-btn" type="button" onClick={() => copy(val)}>복사</button>
                  </div>
                );
              })}
              <div className="acct-row">
                <label>상태</label>
                <select value={acc.status} onChange={(e) => setField('status', e.target.value)} className="acct-input">
                  <option value="active">운영 (active)</option>
                  <option value="suspended">정지 (suspended)</option>
                  <option value="retired">종료 (retired)</option>
                </select>
                <select value={acc.platform} onChange={(e) => setField('platform', e.target.value)} className="acct-input" style={{ maxWidth: 120 }}>
                  <option value="instagram">instagram</option>
                  <option value="tiktok">tiktok</option>
                </select>
              </div>
              <div className="acct-modal__foot">
                <span className="acct-msg">{msg}{acc.source ? ` · 출처: ${acc.source}` : ''}</span>
                <span className="acct-foot-btns">
                  {!acc.isPrimary && <button className="acct-btn" onClick={makePrimary} disabled={saving}>메인으로</button>}
                  <button className="acct-btn acct-btn--save" onClick={saveAcc} disabled={saving}>
                    {saving ? '저장 중…' : '저장'}
                  </button>
                </span>
              </div>
            </>)}
          </>
        )}
      </div>
    </div>
  );
};

// 이 페이지 전용 admin 비번(0987) — 개별 어필리에이트 페이지 비번(3950)과 별개.
// 전 어필리에이트의 payout/매출을 다 보여줘서 admin 전용.
const OVERVIEW_PASSWORD = '0987';

const AffiliateOverview: React.FC = () => {
  // 비번 한 번 통과하면 이후 방문(새로고침·새 탭)에서 다시 안 묻는다.
  const [authed, setAuthed] = useState(() => isRemembered('overview', OVERVIEW_PASSWORD));
  const [acctHandle, setAcctHandle] = useState<string | null>(null); // 계정정보 팝업 대상 handle
  const [notesRow, setNotesRow] = useState<{ handle: string; notes: string } | null>(null); // 메모 팝업 대상
  const [password, setPassword] = useState('');
  // 온보딩 퍼널(초대→조인→환영DM→답장→등록→게시). 초대와 active 사이가 어디서도 안 보여서
  // 문제 유무·규모를 매번 사람이 물어봐야 했다(2026-09-11) → 상시 노출로 바꾼다.
  const [funnel, setFunnel] = useState<FunnelData | null>(null);
  const [funnelDays, setFunnelDays] = useState<30 | 0>(30);   // 기본 30일, 0=전체 누적
  const [funnelOpen, setFunnelOpen] = useState<'notJoined' | 'noReply' | 'noAccount' | null>(null);

  // 퍼널은 요약 카드와 독립적으로 로드한다 — recruit 서버가 죽어도 본 테이블은 그대로 보이게.
  const fetchFunnel = React.useCallback(async (days: 30 | 0) => {
    try {
      const r = await fetch(`${API_URL}/api/stripe/onboarding-funnel?days=${days}`);
      const j = await r.json();
      setFunnel(j && j.ok ? (j as FunnelData) : null);
    } catch { setFunnel(null); }
  }, []);
  useEffect(() => { fetchFunnel(funnelDays); }, [funnelDays, fetchFunnel]);
  const [data, setData] = useState<OverviewData | null>(null);
  const [foundVia, setFoundVia] = useState<Record<string, FoundVia>>({});
  // 발굴 키워드는 recruiting 서버(별도 DB)에서 이메일로 조인. overview 로드 후 한 번 가져온다.
  useEffect(() => {
    const emails = (data?.affiliates || []).map(r => (r.email || '').toLowerCase().trim()).filter(Boolean);
    if (!emails.length) return;
    fetch(`${RECRUIT_API}/found-via`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ emails }) })
      .then(r => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then(j => setFoundVia(j.results || {}))
      .catch(() => { /* recruiting 서버 불가 → 칼럼은 — 로 남김 */ });
  }, [data]);
  const emailByRoute = useMemo(
    () => new Map((data?.affiliates || []).map(r => [r.route, (r.email || '').toLowerCase().trim()])),
    [data]
  );
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [sortKey, setSortKey] = useState<SortKey>('totalRevenue');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  // When on, hide inactive affiliates. Defaults to on so the page opens on the
  // active roster (inactive/ended creators are noise for the day-to-day view).
  const [activeOnly, setActiveOnly] = useState(true);

  // Handles currently being saved (email or status), to disable inputs briefly.
  const [savingHandle, setSavingHandle] = useState<string | null>(null);
  // Local email draft per handle so typing doesn't fight the fetched data.
  const [emailDrafts, setEmailDrafts] = useState<Record<string, string>>({});
  // Winner-yield recompute (playbook section button). Proxied by the backend to
  // the recruiting server, which re-joins live affiliate performance × discovery
  // keywords and refreshes the category weights steering keyword discovery.
  // The static playbook text stays the 2026-09 snapshot; this panel shows the
  // fresh numbers so drift is visible without a frontend redeploy.
  const [recomputing, setRecomputing] = useState(false);
  const [recompute, setRecompute] = useState<RecomputeResult | null>(null);

  const runRecompute = async () => {
    setRecomputing(true);
    setRecompute(null);
    try {
      const r = await adminFetch(`${API_URL}/api/stripe/winner-yield-recompute`, { method: 'POST' });
      setRecompute(await r.json());
    } catch (e) {
      setRecompute({ ok: false, error: e instanceof Error ? e.message : 'request failed' });
    } finally {
      setRecomputing(false);
    }
  };

  const fetchOverview = async (force = false) => {
    setLoading(true);
    setError(null);
    try {
      // roster는 이제 백엔드가 DB에서 조립한다(config 폐지). body 불필요 — 서버가 무시.
      const res = await fetch(`${API_URL}/api/stripe/affiliate-overview${force ? '?force=1' : ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({})
      });
      const json = await res.json();
      if (json.success) {
        setData(json.data);
        // Seed email drafts from server values.
        const drafts: Record<string, string> = {};
        for (const r of json.data.affiliates as OverviewRow[]) drafts[r.handle] = r.email;
        setEmailDrafts(drafts);
      } else {
        setError(json.error || 'Failed to load overview');
      }
    } catch {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (authed) fetchOverview();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authed]);

  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    if (password === OVERVIEW_PASSWORD) {
      setAuthed(true);
      remember('overview', OVERVIEW_PASSWORD);
    } else {
      alert('Incorrect password');
    }
  };

  // Persist one editable field for a handle, then patch it into local state.
  const saveProfile = async (handle: string, patch: { email?: string; status?: string; w9Status?: string; notes?: string }) => {
    setSavingHandle(handle);
    try {
      const res = await fetch(`${API_URL}/api/stripe/affiliate-profile/${encodeURIComponent(handle)}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(patch)
      });
      const json = await res.json();
      if (json.success) {
        setData(prev => prev && {
          ...prev,
          affiliates: prev.affiliates.map(r =>
            r.handle === handle ? { ...r, email: json.data.email, status: json.data.status, w9Status: json.data.w9Status ?? r.w9Status, notes: json.data.notes ?? r.notes } : r
          )
        });
      } else {
        alert(json.error || 'Failed to save');
      }
    } catch {
      alert('Failed to save');
    } finally {
      setSavingHandle(null);
    }
  };



  const toggleSort = (key: SortKey) => {
    if (sortKey === key) {
      setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortKey(key);
      // Text sorts default A→Z; numeric sorts default high→low.
      // startedAt은 오래된 사람부터(=먼저 시작한 순)가 기본이라 asc.
      setSortDir(key === 'route' || key === 'status' || key === 'startedAt' ? 'asc' : 'desc');
    }
  };

  const sortedRows = useMemo(() => {
    if (!data) return [];
    const rows = data.affiliates.filter(r => !activeOnly || r.status === 'active');
    rows.sort((a, b) => {
      let cmp: number;
      if (sortKey === 'route') {
        cmp = a.route.localeCompare(b.route);
      } else if (sortKey === 'status') {
        cmp = a.status.localeCompare(b.status);
      } else if (sortKey === 'convRate') {
        // Rows without signups sort below 0% instead of throwing NaN into the mix.
        cmp = (convRateOf(a) ?? -1) - (convRateOf(b) ?? -1);
      } else if (sortKey === 'rpm' || sortKey === 'cpm') {
        // RPM/CPM은 행 필드가 아니라 파생값이라 a[sortKey]로는 NaN이 된다.
        // 조회수 0(분모 없음)은 -1로 내려 정렬에서 맨 아래로 보낸다.
        const f = sortKey === 'rpm' ? rpmOf : cpmOf;
        cmp = (f(a) ?? -1) - (f(b) ?? -1);
      } else if (sortKey === 'stage') {
        // Stage 정렬 = 진행 주차 순. 라벨 가나다순은 쓸모가 없다(웜업↔포스팅 순서가
        // 알파벳과 무관). 시작일이 없거나 종료·이탈자는 주차가 없으므로 -1로 맨 아래.
        const wk = (r: OverviewRow) =>
          (r.status === 'inactive' || r.status === 'ended') ? -1 : (weekFromStart(r.warmupStartedAt) ?? -1);
        cmp = wk(a) - wk(b);
      } else if (sortKey === 'startedAt') {
        // 시작일. 값이 없으면 항상 맨 아래로(오름·내림 어느 쪽이든) 가도록 0으로 두고
        // 아래 dir 반전 뒤에 남지 않게 별도 처리한다.
        const ts = (r: OverviewRow) => {
          const t = r.warmupStartedAt ? new Date(r.warmupStartedAt).getTime() : NaN;
          return Number.isFinite(t) ? t : null;
        };
        const ta = ts(a), tb = ts(b);
        if (ta == null && tb == null) cmp = 0;
        else if (ta == null) return 1;   // dir 반전 전에 확정 — 빈 값은 늘 아래
        else if (tb == null) return -1;
        else cmp = ta - tb;
      } else {
        cmp = (a[sortKey] as number) - (b[sortKey] as number);
      }
      return sortDir === 'asc' ? cmp : -cmp;
    });
    return rows;
  }, [data, sortKey, sortDir, activeOnly]);

  const sortArrow = (key: SortKey) =>
    sortKey === key ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';

  if (!authed) {
    return (
      <div className="affiliate-overview">
        <form className="affiliate-overview__login" onSubmit={handleLogin}>
          <h1 className="affiliate-overview__login-title">Affiliate Overview</h1>
          <input
            type="password"
            placeholder="Enter password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            className="affiliate-overview__input"
            autoFocus
          />
          <button type="submit" className="affiliate-overview__btn">Enter</button>
        </form>
      </div>
    );
  }

  // Totals reflect the current filter (active-only hides inactive rows).
  const totals = data ? sortedRows.reduce((acc, r) => ({
    totalSignups: acc.totalSignups + r.totalSignups,
    paidCustomers: acc.paidCustomers + r.paidCustomers,
    totalRevenue: Math.round((acc.totalRevenue + r.totalRevenue) * 100) / 100,
    bonus: Math.round((acc.bonus + r.bonus) * 100) / 100,
    totalPaid: Math.round((acc.totalPaid + r.totalPaid) * 100) / 100,
    views30d: acc.views30d + r.views30d,
    views7d: acc.views7d + r.views7d,
    viewsAll: acc.viewsAll + (r.viewsAll || 0),
    paid30d: acc.paid30d + (r.paid30d || 0)
  }), { totalSignups: 0, paidCustomers: 0, totalRevenue: 0, bonus: 0, totalPaid: 0, views30d: 0, views7d: 0, viewsAll: 0, paid30d: 0 }) : null;

  // Roster-wide audience distribution (current filter), for the totals row.
  const audienceTotals = (() => {
    if (!data) return undefined;
    const byCountry = new Map<string, number>();
    for (const r of sortedRows) {
      for (const d of r.countryDistribution || []) {
        byCountry.set(d.country, (byCountry.get(d.country) || 0) + d.count);
      }
    }
    return [...byCountry.entries()]
      .map(([country, count]) => ({ country, count }))
      .sort((a, b) => b.count - a.count);
  })();

  // Last-30d payout is a grand total from the server (not per-row), so it isn't
  // recomputed by the active-only filter.
  const payout30d = data?.totals.payout30d ?? 0;
  const activeCount = data ? data.affiliates.filter(r => r.status === 'active').length : 0;

  // Survey attribution captures only part of true affiliate-driven conversions,
  // so scale the counted numbers up by ~7× for an estimated true contribution
  // shown alongside the actuals. (Empirical multiplier, not a strict 1/0.08.)
  const estMultiplier = 7;

  return (
    <div className="affiliate-overview">
      {acctHandle && <AccountModal handle={acctHandle} onClose={() => { setAcctHandle(null); fetchOverview(); }} />}
      {notesRow && (
        <NotesModal
          handle={notesRow.handle}
          initial={notesRow.notes}
          onClose={() => setNotesRow(null)}
          onSave={(text) => saveProfile(notesRow.handle, { notes: text })}
        />
      )}
      <div className="affiliate-overview__container">
        <div className="affiliate-overview__header">
          <div>
            <h1 className="affiliate-overview__title">Affiliate Overview</h1>
            <p className="affiliate-overview__subtitle">
              {activeOnly ? `${activeCount} active` : `${data ? data.affiliates.length : 0} affiliates`} · combined performance
            </p>
          </div>
          <div className="affiliate-overview__header-actions">
            <AdminNav />
            <label className="affiliate-overview__filter">
              <input
                type="checkbox"
                checked={activeOnly}
                onChange={e => setActiveOnly(e.target.checked)}
              />
              Active only
            </label>
            <button
              type="button"
              className="affiliate-overview__refresh"
              onClick={() => fetchOverview(true)}
              disabled={loading}
              title="Recompute now (bypass the 10-min cache)"
            >
              {loading ? 'Loading…' : 'Refresh'}
            </button>
          </div>
        </div>

        {error && <p className="affiliate-overview__error">{error}</p>}

        {/* 온보딩 퍼널 — 초대(recruit)와 active(이 화면) 사이 구간. 이 구간이 어디서도 안 보여
            문제 유무·규모를 매번 사람이 물어봐야 했다(2026-09-11). 상시 노출로 바꾼다.
            recruit 서버가 죽으면 funnel=null이라 이 블록만 사라지고 본 테이블은 그대로 뜬다. */}
        {funnel && (() => {
          const c = funnel.counts;
          const pct = (a: number, b: number) => (b > 0 ? Math.round((a / b) * 100) : 0);
          const steps: Array<{ key: string; label: string; value: number; of?: number }> = [
            { key: 'invited', label: '초대', value: c.invited },
            { key: 'joined', label: '조인', value: c.joined, of: c.invited },
            { key: 'replied', label: '답장', value: c.replied, of: c.joined },
            // 계약 = DocuSeal 서명 완료. 계약서를 계정 생성보다 먼저 태우는 순서 변경의
            // 효과를 보는 칸이라 '답장' 다음에 둔다(옛 건은 백필했다).
            { key: 'contracted', label: '계약', value: c.contracted ?? 0, of: c.replied },
            { key: 'registered', label: '계정 등록', value: c.registered, of: c.replied },
            { key: 'posted', label: '게시', value: c.posted, of: c.registered },
          ];
          const nj = funnel.stalled.notJoined;
          const nr = funnel.stalled.noReply;
          const na = funnel.stalled.noAccount;
          const open = funnelOpen === 'notJoined' ? nj
            : funnelOpen === 'noReply' ? nr
              : funnelOpen === 'noAccount' ? na : null;
          return (
            <div className="affiliate-overview__funnel">
              <div className="affiliate-overview__funnel-head">
                <span className="affiliate-overview__funnel-title">온보딩 퍼널</span>
                <div className="affiliate-overview__funnel-toggle">
                  <button
                    className={funnelDays === 30 ? 'is-on' : ''}
                    onClick={() => { setFunnelDays(30); setFunnelOpen(null); }}
                  >최근 30일</button>
                  <button
                    className={funnelDays === 0 ? 'is-on' : ''}
                    onClick={() => { setFunnelDays(0); setFunnelOpen(null); }}
                  >전체 누적</button>
                </div>
              </div>
              <div className="affiliate-overview__funnel-steps">
                {steps.map((st, i) => (
                  <React.Fragment key={st.key}>
                    {i > 0 && <span className="affiliate-overview__funnel-arrow">→</span>}
                    <div className="affiliate-overview__funnel-step">
                      <span className="affiliate-overview__funnel-label">{st.label}</span>
                      <span className="affiliate-overview__funnel-value">{st.value.toLocaleString()}</span>
                      <span className="affiliate-overview__funnel-sub">
                        {st.of === undefined ? '\u00a0' : `${pct(st.value, st.of)}%`}
                      </span>
                    </div>
                  </React.Fragment>
                ))}
              </div>
              {(!!(nj && nj.total) || !!(nr && nr.total) || !!(na && na.total)) && (
                <div className="affiliate-overview__funnel-stall">
                  <span className="affiliate-overview__funnel-stall-label">정체 중</span>
                  {!!(nj && nj.total) && (
                    <button
                      className={funnelOpen === 'notJoined' ? 'is-on' : ''}
                      onClick={() => setFunnelOpen(funnelOpen === 'notJoined' ? null : 'notJoined')}
                      title="초대받고 3일이 지나도 슬랙에 조인하지 않은 사람"
                    >초대 후 미조인 <b>{nj.total}</b></button>
                  )}
                  {!!(nr && nr.total) && (
                    <button
                      className={funnelOpen === 'noReply' ? 'is-on' : ''}
                      onClick={() => setFunnelOpen(funnelOpen === 'noReply' ? null : 'noReply')}
                      title="환영 DM을 받고 7일이 지나도 답이 없는 사람 (88%가 7일 내에 답한다)"
                    >환영DM 후 무응답 <b>{nr.total}</b></button>
                  )}
                  {!!(na && na.total) && (
                    <button
                      className={funnelOpen === 'noAccount' ? 'is-on' : ''}
                      onClick={() => setFunnelOpen(funnelOpen === 'noAccount' ? null : 'noAccount')}
                      title="답장까지 했는데 계정정보를 안 낸 사람 — 실측 최대 누수 구간(답장 173 → 등록 58)"
                    >답장 후 계정 미제출 <b>{na.total}</b></button>
                  )}
                </div>
              )}
              {open && open.rows.length > 0 && (
                <div className="affiliate-overview__funnel-list">
                  {open.rows.map((r, i) => (
                    <div className="affiliate-overview__funnel-row" key={`${r.email || r.username || i}`}>
                      <span className="affiliate-overview__funnel-who">{r.username || r.email || '(이름 없음)'}</span>
                      <span className="affiliate-overview__funnel-days">{r.days}일 경과</span>
                      <span className="affiliate-overview__funnel-meta">
                        {r.reminders !== undefined ? `리마인더 ${r.reminders}회` : ''}
                        {r.followups !== undefined ? `팔로업 ${r.followups}회` : ''}
                      </span>
                    </div>
                  ))}
                  {open.total > open.rows.length && (
                    <div className="affiliate-overview__funnel-more">
                      외 {open.total - open.rows.length}명 더 있음
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })()}

        {/* Grand-total summary cards */}
        {totals && (
          <div className="affiliate-overview__cards">
            <div className="affiliate-overview__card">
              <p className="affiliate-overview__card-label">Total Revenue</p>
              <p className="affiliate-overview__card-value">{formatUSD(totals.totalRevenue)}</p>
              <p className="affiliate-overview__card-est" title="Estimated true contribution (~7× the survey-attributed number)">
                ~{formatUSD(totals.totalRevenue * estMultiplier)} est.
              </p>
            </div>
            <div className="affiliate-overview__card">
              <p className="affiliate-overview__card-label">Paid Customers</p>
              <p className="affiliate-overview__card-value">{totals.paidCustomers.toLocaleString()}</p>
              <p className="affiliate-overview__card-est" title="Estimated true contribution (~7× the survey-attributed number)">
                ~{Math.round(totals.paidCustomers * estMultiplier).toLocaleString()} est.
              </p>
            </div>
            <div className="affiliate-overview__card affiliate-overview__card--bonus">
              <p className="affiliate-overview__card-label">Total Payout</p>
              <p className="affiliate-overview__card-value">{formatUSD(totals.totalPaid)}</p>
            </div>
            <div className="affiliate-overview__card">
              <p className="affiliate-overview__card-label">Payout · Last 30d</p>
              <p className="affiliate-overview__card-value">{formatUSD(payout30d)}</p>
            </div>
          </div>
        )}

        {loading && !data ? (
          <p className="affiliate-overview__loading">Loading affiliates…</p>
        ) : data ? (
          <div className="affiliate-overview__table-wrap">
            <table className="affiliate-overview__table">
              <thead>
                <tr>
                  <th className="affiliate-overview__th--sortable affiliate-overview__sticky-col" onClick={() => toggleSort('route')}>
                    Affiliate{sortArrow('route')}
                  </th>
                  <th className="affiliate-overview__th--sortable" title="온보딩 단계 + 진행 주차(W1=시작 당일부터). 정렬은 주차 순 — 종료·이탈자와 시작일 없는 행은 맨 아래." onClick={() => toggleSort('stage')}>
                    Stage{sortArrow('stage')}
                  </th>
                  <th className="affiliate-overview__th--sortable" title="시작일(warmupStartedAt). 주차 계산의 원점이다. 빈 값은 정렬 시 항상 맨 아래." onClick={() => toggleSort('startedAt')}>
                    Started{sortArrow('startedAt')}
                  </th>
                  <th>Instagram</th>
                  <th>Account</th>
                  <th>TikTok</th>
                  <th title="발굴 키워드 — 콜드메일 발송 로그를 이메일로 recruiting DB와 조인해, 그 사람을 처음 찾아낸 검색어(최초 귀속). 🔎 = 유저명(핸들) 검색, G· = 구글. 호버하면 다른 검색어. — = 발송 기록 없음(인바운드·수동).">Found via</th>
                  <th>Country</th>
                  <th>Age</th>
                  <th>Ethnicity</th>
                  <th>Email</th>
                  <th title="개인 구글시트 링크(임시, 시트→DB 정본화 검증용)">Sheet</th>
                  <th className="affiliate-overview__th--num affiliate-overview__th--sortable" onClick={() => toggleSort('totalSignups')}>
                    Signups{sortArrow('totalSignups')}
                  </th>
                  <th className="affiliate-overview__th--num affiliate-overview__th--sortable" onClick={() => toggleSort('paidCustomers')}>
                    Paid{sortArrow('paidCustomers')}
                  </th>
                  <th className="affiliate-overview__th--num affiliate-overview__th--sortable" title="가입→결제 전환율 = Paid ÷ Signups" onClick={() => toggleSort('convRate')}>
                    Conv%{sortArrow('convRate')}
                  </th>
                  <th title="유료 전환 고객의 국가 상위 3개 (Stripe 청구지 기준). 국기에 호버하면 전체 퍼센트.">Audience</th>
                  <th className="affiliate-overview__th--num affiliate-overview__th--sortable" title="최근 30일 게시물의 조회수 합(게시일 기준). 괄호는 직전 30일 대비 증감 — 직전이 0이면 비교 불가라 표시 안 함." onClick={() => toggleSort('views30d')}>
                    Views 30d{sortArrow('views30d')}
                  </th>
                  <th className="affiliate-overview__th--num affiliate-overview__th--sortable" title="수집된 릴의 조회수 합. ⚠️ '전 기간'이 아니다 — 수집은 90일 창 + 증분(마지막 저장일 이후)이라, 그 크리에이터를 처음 수집한 날 이전 게시물은 들어와 있지 않다(백필 전까지). 괄호는 수집된 릴 수." onClick={() => toggleSort('viewsAll')}>
                    Total Views{sortArrow('viewsAll')}
                  </th>
                  <th className="affiliate-overview__th--num affiliate-overview__th--sortable" onClick={() => toggleSort('totalRevenue')}>
                    Revenue{sortArrow('totalRevenue')}
                  </th>
                  <th className="affiliate-overview__th--num affiliate-overview__th--sortable" title="RPM = 조회수 1,000회당 매출(매출 ÷ 30일 조회수 × 1000). 조회수는 터지는데 매출이 안 붙는 크리에이터를 잡아낸다. 조회수 0이면 '—'." onClick={() => toggleSort('rpm')}>
                    RPM{sortArrow('rpm')}
                  </th>
                  <th className="affiliate-overview__th--num affiliate-overview__th--sortable" title="CPM = 조회수 1,000회당 비용(최근 30일 지급액 ÷ 30일 조회수 × 1000). ⚠️ 분자는 누적 지급액이 아니라 최근 30일분 — 분모가 30일이라 기간을 맞춘 것이다. 누적으로 나누면 오래 일한 사람이 최대 3.9배 비싸게 찍힌다. 조회수 0이면 '—'." onClick={() => toggleSort('cpm')}>
                    CPM{sortArrow('cpm')}
                  </th>
                  <th className="affiliate-overview__th--num affiliate-overview__th--sortable" onClick={() => toggleSort('bonus')}>
                    Bonus{sortArrow('bonus')}
                  </th>
                  <th className="affiliate-overview__th--num" title="밀린 기본급(완료된 주차 기준). 현재 진행중인 이번 주는 제외.">Retainer Pending</th>
                  <th className="affiliate-overview__th--num" title="미지급 인센티브 = 매출귀속 bonus − 지급된 bonus. 음수는 과지급(추가 0).">Bonus Pending</th>
                  <th title="Stripe payout onboarding status">Stripe</th>
                  <th title="W-9 tax form collection status (Inkle Books)">W-9</th>
                  <th title="Whether the creator actually put their ref link + coupon code in their IG bio (auto-checked hourly). Attribution fails if missing.">Bio</th>
                  <th title="릴 데이터를 마지막으로 최신화한 시각(KST). refreshAffiliateReels가 이 크리에이터를 훑은 때 — 새 릴이 0건이어도 찍힌다. 오래됐으면 게시·정산 판정이 낡은 데이터 위에서 이뤄진 것이다. 빈 칸 = 아직 안 돎(inactive는 수집 대상 제외).">Reels Synced</th>
                  <th title="희윤 Slack DM을 마지막으로 최신화한 시각(KST). syncConvos가 이 크리에이터 대화를 훑은 때 — 새 메시지가 0건이어도 찍힌다. 오래됐으면 미답 감지가 낡은 대화 위에서 이뤄진 것이다.">DM Synced</th>
                  <th title="크레딧을 지급할 ssemble.com 가입 이메일. 비어 있으면 크레딧 지급 자체가 불가능하다(addCreditsToUser가 404를 낸다).">Signup email</th>
                  <th title="Ssemble product credits for tutorial recording. Auto-checked from the creator's signup email against the users DB.">Credits</th>
                  <th className="affiliate-overview__th--sortable" onClick={() => toggleSort('status')}>
                    Status{sortArrow('status')}
                  </th>
                  <th title="운영 메모. 종료자의 계정 회수 단계(로그아웃 대기 / 2FA 리셋 완료) 등 status로는 안 보이는 진행 상태를 적어둔다. 칸을 벗어나면 저장된다.">Notes</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map(r => (
                  <tr
                    key={r.route || r.handle}
                    className={r.status === 'inactive' ? 'affiliate-overview__row--inactive' : ''}
                  >
                    <td className="affiliate-overview__sticky-col">
                      {r.route ? (
                        <Link className="affiliate-overview__handle" to={`/${r.route}`}>
                          {r.igHandle || r.handle}
                        </Link>
                      ) : (
                        <span className="affiliate-overview__handle">{r.igHandle || r.handle}</span>
                      )}
                      {r.error && (
                        <span className="affiliate-overview__row-error" title={r.error}>⚠︎</span>
                      )}
                    </td>
                    <td>
                      {stageBadge(r)}
                      {weekBadge(r)}
                    </td>
                    <td className="affiliate-overview__started">
                      {r.warmupStartedAt
                        ? String(r.warmupStartedAt).slice(0, 10)
                        : <span className="affiliate-overview__muted">—</span>}
                    </td>
                    <td>
                      <a
                        className="affiliate-overview__ig"
                        href={instagramUrl(r.igHandle || r.handle)}
                        target="_blank"
                        rel="noreferrer"
                      >
                        @{r.igHandle || r.handle}
                      </a>
                    </td>
                    <td>
                      <button
                        type="button"
                        className={`affiliate-overview__acct-btn${r.hasAccount ? ' affiliate-overview__acct-btn--set' : ''}`}
                        onClick={() => setAcctHandle(r.handle)}
                        title={r.hasAccount ? '계정정보 보기/수정' : '계정정보 없음 — 추가'}
                      >
                        🔑 {r.hasAccount ? '있음' : '없음'}
                      </button>
                    </td>
                    <td>
                      {r.tiktokUrl ? (
                        <a
                          className="affiliate-overview__ig"
                          href={r.tiktokUrl}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {r.tiktokUrl.replace(/^https?:\/\/(www\.)?tiktok\.com\//, '@').replace(/^@@/, '@')}
                        </a>
                      ) : (
                        <span className="affiliate-overview__muted">—</span>
                      )}
                    </td>
                    <td className="affiliate-overview__found-via">{foundViaCell(foundVia[(r.email || '').toLowerCase().trim()])}</td>
                    <td>
                      {r.country
                        ? <span className="affiliate-overview__country">
                            {flagEmoji(r.country) && (
                              <span className="affiliate-overview__flag">{flagEmoji(r.country)}</span>
                            )}
                            {r.country}
                          </span>
                        : <span className="affiliate-overview__muted">—</span>}
                    </td>
                    <td>{r.ageRange || <span className="affiliate-overview__muted">—</span>}</td>
                    <td className="affiliate-overview__ethnicity">
                      {r.ethnicity
                        ? <span className="affiliate-overview__ethnicity-val">
                            <span className="affiliate-overview__ethnicity-emoji">{ethnicityEmoji(r.ethnicity)}</span>
                            {r.ethnicity.replace(/_/g, ' ')}
                          </span>
                        : <span className="affiliate-overview__muted">—</span>}
                    </td>
                    <td>
                      <input
                        type="email"
                        className="affiliate-overview__email-input"
                        placeholder="email…"
                        value={emailDrafts[r.handle] ?? ''}
                        disabled={savingHandle === r.handle}
                        onChange={e =>
                          setEmailDrafts(d => ({ ...d, [r.handle]: e.target.value }))
                        }
                        onBlur={() => {
                          const draft = (emailDrafts[r.handle] ?? '').trim();
                          if (draft !== (r.email || '')) saveProfile(r.handle, { email: draft });
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                        }}
                      />
                    </td>
                    <td>
                      {r.sheetUrl
                        ? <a href={r.sheetUrl} target="_blank" rel="noreferrer" title={r.sheetUrl}>📄</a>
                        : <span style={{ color: '#bbb' }}>—</span>}
                    </td>
                    <td className="affiliate-overview__td--num">{r.totalSignups}</td>
                    <td className="affiliate-overview__td--num">{r.paidCustomers}</td>
                    <td className="affiliate-overview__td--num" title={`${r.paidCustomers} paid / ${r.totalSignups} signups`}>
                      {convRateOf(r) !== null
                        ? pct1(convRateOf(r) as number)
                        : <span className="affiliate-overview__muted">—</span>}
                    </td>
                    <td>{audienceCell(r.countryDistribution)}</td>
                    <td className="affiliate-overview__td--num" title={`${r.views30d.toLocaleString()} views · ${r.reelCount} reels (last 30d)`}>
                      {r.views30d > 0 ? formatViews(r.views30d) : <span className="affiliate-overview__muted">—</span>}
                      {(() => {
                        const d = deltaOf(r.views30d, r.viewsPrev30d);
                        if (d === null || r.views30d === 0) return null;
                        const up = d >= 0;
                        return (
                          <span
                            className="affiliate-overview__rate"
                            style={{ color: up ? '#1f7a4d' : '#b23b32' }}
                            title={`직전 30일 ${r.viewsPrev30d.toLocaleString()} → 최근 30일 ${r.views30d.toLocaleString()}`}
                          >
                            {' '}{up ? '▲' : '▼'}{Math.abs(Math.round(d * 100))}%
                          </span>
                        );
                      })()}
                    </td>
                    <td className="affiliate-overview__td--num" title={`${(r.viewsAll || 0).toLocaleString()} views · ${r.reelCountAll || 0} reels (all time)`}>
                      {r.viewsAll > 0 ? formatViews(r.viewsAll) : <span className="affiliate-overview__muted">—</span>}
                      {r.reelCountAll > 0 && (
                        <span className="affiliate-overview__rate"> ({r.reelCountAll})</span>
                      )}
                    </td>
                    <td className="affiliate-overview__td--num">{formatUSD(r.totalRevenue)}</td>
                    <td className="affiliate-overview__td--num" title="조회수 1,000회당 매출">
                      {rpmOf(r) !== null
                        ? formatUSD(rpmOf(r) as number)
                        : <span className="affiliate-overview__muted">—</span>}
                    </td>
                    <td className="affiliate-overview__td--num" title={`최근 30일 지급 ${formatUSD(r.paid30d || 0)} ÷ 30일 조회수`}>
                      {cpmOf(r) !== null
                        ? ((r.paid30d || 0) > 0
                            ? formatUSD(cpmOf(r) as number)
                            : <span className="affiliate-overview__muted" title="최근 30일 지급 없음">$0.00</span>)
                        : <span className="affiliate-overview__muted">—</span>}
                    </td>
                    <td className="affiliate-overview__td--num">
                      {formatUSD(r.bonus)}
                      {r.totalRevenue > 0 && (
                        <span className="affiliate-overview__rate"> ({pct(r.blendedRate)})</span>
                      )}
                    </td>
                    <td className="affiliate-overview__td--num">
                      {typeof r.retainerPending === 'number' && r.retainerPending > 0
                        ? <span style={{ color: '#b8730b', fontWeight: 600 }}>
                            {formatUSD(r.retainerPending)}
                            {r.retainerOwedWeeks && r.retainerOwedWeeks.length > 0 && (
                              <span className="affiliate-overview__rate"> ({r.retainerOwedWeeks.map((w) => `W${w}`).join(',')})</span>
                            )}
                          </span>
                        : <span className="affiliate-overview__muted">—</span>}
                    </td>
                    <td className="affiliate-overview__td--num">
                      {typeof r.bonusPending === 'number' && r.bonusPending > 0.005
                        ? <span style={{ color: '#b8730b', fontWeight: 600 }}>{formatUSD(r.bonusPending)}</span>
                        : <span className="affiliate-overview__muted">—</span>}
                    </td>
                    <td>
                      {(() => {
                        const ps = r.payoutStatus || 'none';
                        const label = ps === 'enabled' ? '✅ Connected' : ps === 'started' ? '⏳ In progress' : '— Not started';
                        return <span className={`affiliate-overview__payout affiliate-overview__payout--${ps}`} title="Stripe payout onboarding">{label}</span>;
                      })()}
                    </td>
                    <td>
                      {/* W-9 수집 상태 — Inkle Books에서 리뷰 후 admin이 직접 토글(자동 동기화 없음). */}
                      <select
                        className={`affiliate-overview__w9 affiliate-overview__w9--${r.w9Status || 'none'}`}
                        value={r.w9Status || 'none'}
                        disabled={savingHandle === r.handle}
                        onChange={e => saveProfile(r.handle, { w9Status: e.target.value })}
                        title="W-9 tax form (Inkle Books). Review in Inkle, then set here."
                      >
                        <option value="none">— Not requested</option>
                        <option value="requested">⏳ Requested</option>
                        <option value="received">✅ Received</option>
                        <option value="not_required">N/A (non-US)</option>
                      </select>
                    </td>
                    <td>
                      {/* Bio 상태 — runBioCheck.js가 IG 바이오의 ref링크·쿠폰코드 포함 여부를 매시간 자동 판정(읽기전용 배지). */}
                      {(() => {
                        const bs = r.bioStatus || 'not_checked';
                        const map: Record<string, { label: string; title: string }> = {
                          ok: { label: '✅ OK', title: 'ref link + coupon both in bio' },
                          missing_coupon: { label: '⚠️ No coupon', title: 'ref link present, coupon code missing' },
                          missing_ref: { label: '⚠️ No ref', title: 'coupon present, ref link missing' },
                          missing_both: { label: '❌ Neither', title: 'neither ref link nor coupon in bio' },
                          no_account: { label: '— No IG', title: 'no IG account on file' },
                          private: { label: '🔒 Private', title: 'IG account is private, cannot read bio' },
                          error: { label: '⚠️ Error', title: 'failed to fetch bio (rate limit / blocked)' },
                          not_checked: { label: '· Pending', title: 'not checked yet (runs ~1 day after ref given)' },
                        };
                        const m = map[bs] || map.not_checked;
                        return <span className={`affiliate-overview__bio affiliate-overview__bio--${bs}`} title={m.title}>{m.label}</span>;
                      })()}
                    </td>
                    {/* 릴 최신화 — 하루 1회 크론이라 36h 넘으면 경고. */}
                    <td>{syncCell(r.reelsSyncedAt, 36, r)}</td>
                    {/* DM 최신화 — 매시간 루프라 6h 넘으면 경고. */}
                    <td>{syncCell(r.convoSyncedAt, 6, r)}</td>
                    {/* 크레딧 지급 대상 이메일 — 없으면 지급 자체가 불가(404). */}
                    <td className="affiliate-overview__signup-email" title={r.ssembleSignupEmail || '미기록 — 크레딧 지급 불가'}>
                      {r.ssembleSignupEmail || <span className="affiliate-overview__credit--no_email">—</span>}
                    </td>
                    <td>
                      {/* Ssemble 크레딧 — ssembleSignupEmail로 users DB 조회 자동판정(읽기전용 배지). */}
                      {(() => {
                        const cs = r.creditStatus || 'no_email';
                        const bal = r.credits;
                        const cmap: Record<string, { label: string; title: string }> = {
                          granted: { label: bal != null ? `✅ ${bal}` : '✅ Granted', title: `signed up, ${bal ?? '?'} credits on account` },
                          signed_up: { label: '⚠️ 0 left', title: 'signed up but 0 credits — may need a top-up' },
                          not_found: { label: '❌ No signup', title: 'signup email on file but no Ssemble account found — ask creator to confirm email' },
                          no_email: { label: '· No email', title: 'no signup email recorded yet (grant credits & record email in onboarding)' },
                        };
                        const cm = cmap[cs] || cmap.no_email;
                        return <span className={`affiliate-overview__credit affiliate-overview__credit--${cs}`} title={cm.title}>{cm.label}</span>;
                      })()}
                    </td>
                    <td>
                      <select
                        className={`affiliate-overview__status affiliate-overview__status--${r.status}`}
                        value={r.status}
                        disabled={savingHandle === r.handle}
                        onChange={e => saveProfile(r.handle, { status: e.target.value })}
                      >
                        <option value="active">active</option>
                        <option value="inactive">inactive</option>
                      </select>
                    </td>
                    <td>
                      <div className="affiliate-overview__notes-cell">
                        {/* 입력 중에 매 글자를 저장하면 요청이 쏟아지므로 blur에서만 저장하고,
                            값이 그대로면 아예 보내지 않는다. 긴 메모는 옆 버튼으로 팝업에서 본다. */}
                        <input
                          className="affiliate-overview__notes"
                          type="text"
                          key={`${r.handle}:${r.notes || ''}`}  /* 팝업 저장 후 표에도 반영되게 */
                          defaultValue={r.notes || ''}
                          placeholder="—"
                          title={r.notes || ''}
                          disabled={savingHandle === r.handle}
                          onBlur={e => {
                            const next = e.target.value.trim();
                            if (next !== (r.notes || '')) {
                              saveProfile(r.handle, { notes: next });
                            }
                          }}
                        />
                        <button
                          type="button"
                          className="affiliate-overview__notes-open"
                          title="메모 전체보기·편집"
                          onClick={() => setNotesRow({ handle: r.handle, notes: r.notes || '' })}
                        >⤢</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
              {totals && (
                <tfoot>
                  <tr className="affiliate-overview__foot">
                    <td className="affiliate-overview__sticky-col">Total ({sortedRows.length})</td>
                    <td></td>{/* Stage */}
                    <td></td>{/* Started */}
                    <td></td>{/* Instagram */}
                    <td></td>{/* Account */}
                    <td></td>{/* TikTok */}
                    <td></td>{/* Country */}
                    <td></td>{/* Age */}
                    <td></td>{/* Ethnicity */}
                    <td></td>{/* Email */}
                    <td></td>{/* Sheet */}
                    <td className="affiliate-overview__td--num">{totals.totalSignups}</td>
                    <td className="affiliate-overview__td--num">{totals.paidCustomers}</td>
                    <td className="affiliate-overview__td--num" title={`${totals.paidCustomers} paid / ${totals.totalSignups} signups`}>
                      {totals.totalSignups > 0 ? pct1(totals.paidCustomers / totals.totalSignups) : '—'}
                    </td>
                    <td>{audienceCell(audienceTotals)}</td>
                    <td className="affiliate-overview__td--num" title={`${totals.views30d.toLocaleString()} views (last 30d)`}>
                      {formatViews(totals.views30d)}
                    </td>
                    <td className="affiliate-overview__td--num" title={`${totals.viewsAll.toLocaleString()} views (all time)`}>
                      {formatViews(totals.viewsAll)}
                    </td>
                    <td className="affiliate-overview__td--num">{formatUSD(totals.totalRevenue)}</td>
                    <td className="affiliate-overview__td--num" title="전체 블렌디드 RPM = 총매출 ÷ 총 30일 조회수 × 1000">
                      {totals.views30d > 0
                        ? formatUSD((totals.totalRevenue / totals.views30d) * 1000)
                        : <span className="affiliate-overview__muted">—</span>}
                    </td>
                    <td className="affiliate-overview__td--num" title="전체 블렌디드 CPM = 최근 30일 총지급 ÷ 총 30일 조회수 × 1000">
                      {totals.views30d > 0
                        ? formatUSD((totals.paid30d / totals.views30d) * 1000)
                        : <span className="affiliate-overview__muted">—</span>}
                    </td>
                    <td className="affiliate-overview__td--num">{formatUSD(totals.bonus)}</td>
                    <td></td>{/* Retainer Pending */}
                    <td></td>{/* Bonus Pending */}
                    <td></td>{/* Stripe */}
                    <td></td>{/* W-9 */}
                    <td></td>{/* Bio */}
                    <td></td>{/* Reels Synced */}
                    <td></td>{/* DM Synced */}
                    <td></td>{/* Signup email */}
                    <td></td>{/* Credits */}
                    <td></td>{/* Status */}
                    <td></td>{/* Notes */}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        ) : null}

        <p className="affiliate-overview__footnote">
          Click an affiliate to open their individual dashboard. Email and status
          save automatically. Bonus rate is 20% (30% for joe after Jun 29).
        </p>

        {/* Data-backed recruiting playbook (static). Derived from the ≥$100-bonus
            cohort above (country + first discovery keyword) measured against the
            ~38.4K distinct creators cold-emailed by the recruiting pipeline
            (recruiting.sqlite, 2026-09 snapshot). */}
        <section className="affiliate-overview__playbook">
          <div className="affiliate-overview__playbook-head">
            <h2 className="affiliate-overview__playbook-title">🎯 Who to recruit next</h2>
            <button
              className="affiliate-overview__recompute-btn"
              onClick={runRecompute}
              disabled={recomputing}
              title="Re-join live affiliate performance with the recruiting DB and refresh the keyword-category weights on the recruiting server"
            >
              {recomputing ? 'Recomputing… (~30s)' : '↻ Recompute from live data'}
            </button>
          </div>
          <p className="affiliate-overview__playbook-sub">
            The 18 affiliates with ≥$100 cumulative bonus — country + the search keyword that found them,
            vs ~38.4K cold emails sent (2026-09)
          </p>
          {recompute && (
            <div className={`affiliate-overview__recompute-result${recompute.ok ? '' : ' affiliate-overview__recompute-result--error'}`}>
              {recompute.ok ? (
                <>
                  <div className="affiliate-overview__recompute-meta">
                    Recomputed {recompute.computed_at ? new Date(recompute.computed_at).toLocaleString() : ''} ·{' '}
                    <strong>{recompute.winners}</strong> winners of {recompute.total_affiliates} affiliates ·{' '}
                    {recompute.wrote ? 'weights file updated on the recruiting server' : 'dry run'}
                  </div>
                  <div className="affiliate-overview__recompute-grid">
                    <div>
                      <div className="affiliate-overview__recompute-h">Winners / 1k emails by category</div>
                      {(recompute.categories || []).map(c => (
                        <div key={c.cat} className="affiliate-overview__recompute-row">
                          <span>{c.cat}</span>
                          <span>{c.per1k.toFixed(2)} <em>({c.winners}/{c.sent.toLocaleString()})</em></span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="affiliate-overview__recompute-h">By country (sent ≥ 200)</div>
                      {(recompute.regions || []).map(r => (
                        <div key={r.region} className="affiliate-overview__recompute-row">
                          <span>{flagEmoji(r.region)} {r.region}</span>
                          <span>{r.per1k.toFixed(2)} <em>({r.winners}/{r.sent.toLocaleString()})</em></span>
                        </div>
                      ))}
                    </div>
                    <div>
                      <div className="affiliate-overview__recompute-h">Applied category weights</div>
                      {Object.entries(recompute.weights || {}).map(([k, v]) => (
                        <div key={k} className="affiliate-overview__recompute-row">
                          <span>{k}</span><span>×{v}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              ) : (
                <div className="affiliate-overview__recompute-meta">
                  Recompute failed: {recompute.error || 'unknown error'}
                  {recompute.error?.includes('already running') && ' — a run is already in progress, try again in ~30s.'}
                </div>
              )}
            </div>
          )}
          <div className="affiliate-overview__arpu">
            {TOP_PERFORMERS.map(row => {
              const tier = row.bonus >= 1000 ? 'high' : row.bonus < 200 ? 'low' : 'mid';
              return (
                <span key={row.route} className={`affiliate-overview__arpu-item affiliate-overview__arpu-item--${tier}`}>
                  <span className="affiliate-overview__arpu-flag">{flagEmoji(row.c)}</span>
                  <span className="affiliate-overview__arpu-code">{row.route}</span>
                  <span className="affiliate-overview__arpu-val">${row.bonus.toLocaleString()}</span>
                  <span className="affiliate-overview__arpu-n">{(() => {
                    const live = foundVia[emailByRoute.get(row.route) || ''];
                    return live?.first_keyword ? keywordLabel(live.first_keyword) : (row.kw || 'inbound');
                  })()}</span>
                </span>
              );
            })}
          </div>
          <ol className="affiliate-overview__playbook-list" style={{ marginTop: 'var(--space-4)' }}>
            <li>
              <strong>Countries: double down on US · GB · CA — stop widening into non-English Europe.</strong>{' '}
              All 18 winners but two are US (10) / GB (4) / CA (2). Yield per 1,000 cold emails: CA <strong>1.6</strong>,
              GB <strong>0.7</strong>, US <strong>0.5</strong> — while FR + ES + AU + NL + BR (~6,900 emails combined)
              produced <strong>zero</strong> $100+ affiliates, and DE (3,219 emails) just one small one (levi, $114).
              GB also punches above its weight on revenue: joe + harrison alone are ~55% of all bonus ever paid.
            </li>
            <li>
              <strong>Best keyword ROI by far: explicit male-UGC terms</strong> (&quot;guy ugc&quot;, &quot;ugcbro&quot;,
              &quot;menugctips&quot;…). Only 358 creators were ever emailed from this category, yet it produced{' '}
              <strong>4 winners (~11 per 1,000 — 5–20× every other category)</strong>, including harrison ($2.1K).
              The pool is small and largely mined — keep hammering fresh variants (ugcguy/ugcdude/menugc/malecreator…)
              in US·GB·CA.
            </li>
            <li>
              <strong>Finance / career / data keywords produce the biggest single winners.</strong> joe ($7.9K, our #1)
              came from &quot;actuarial&quot; / &quot;resume&quot;; jonathan ($757) from &quot;tipping culture&quot;;
              matt from &quot;tradingcards&quot;. ~3.2 winners per 1,000 emails on only 935 sent — the most
              under-invested high-yield category. Expand here: career/analytics/salary/investing angles.
            </li>
            <li>
              <strong>Sports & fitness is the volume engine, not the jackpot.</strong> Most winners by count
              (6 of 18: track, calisthenics, lifting, linebacker, darts) at ~2.2 per 1,000 — but payouts cluster
              $100–660. Worth keeping at current volume, not scaling further.
            </li>
            <li>
              <strong>Dead ends — cut or keep minimal:</strong> tech/coding (1,425 emails, 0 winners),
              gaming (268, 0), grooming/fashion (1,438, 1 marginal — federico $165), and generic long-tail
              keywords (31K+ emails → 2 winners, ~0.06 per 1,000). Broad discovery volume without a
              persona-matched keyword is where almost all outreach spend goes to die.
            </li>
          </ol>
          <p className="affiliate-overview__playbook-note">
            Caveats: 16 of 18 winners came via cold email (tillmann and kyri10 were inbound — the channel that found
            our #3/#4 is not in these keyword stats), and joe + harrison alone dominate revenue, so per-category
            numbers are small-N. Treat these as priorities, not guarantees — trying enough creators still matters most.
          </p>
        </section>
      </div>
    </div>
  );
};

export default AffiliateOverview;
